import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ConfirmSyncDialogProps {
  open: boolean;
  title: string;
  /** Optional — some triggers have no distinct body text beyond `warning`. */
  body?: ReactNode;
  /**
   * A standout warning banner for a consequence worth calling out beyond the body text (data loss,
   * overwriting what a cashier currently sees, …). Omit for an ordinary, low-risk confirmation —
   * the banner is not shown just because a dialog exists.
   */
  warning?: string;
  /** Error-coloured warning + a mandatory acknowledgement checkbox before Confirm enables. */
  destructive?: boolean;
  /** Checkbox label — required when `destructive`. */
  ackLabel?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Shared confirm step for a genuinely consequential Retail Evotor trigger — used internally by
 * {@link SyncTriggerRow} (destructive delete-all rows) and directly by `RetailEvotorStoresPanel`
 * (turning per-store stock export on). As of adr-integration-012/adr-integration-013
 * (2026-08-30) every bulk trigger is tenant-scoped, so an ordinary sync/import/export action no
 * longer needs a confirm step at all — reach for this only when the action is destructive or
 * otherwise consequential enough to warrant a `warning` banner and, for destructive actions, a
 * mandatory acknowledgement checkbox.
 *
 * The action fires (and the dialog closes) immediately on confirm — the caller's own trigger
 * button/row owns the pending state and eventual result, not this dialog.
 */
export function ConfirmSyncDialog({
  open,
  title,
  body,
  warning,
  destructive = false,
  ackLabel,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmSyncDialogProps) {
  const { t } = useTranslation('settings');
  const [acked, setAcked] = useState(false);

  // The ack-checkbox resets every time the dialog opens, per the design spec.
  useEffect(() => {
    if (open) {
      setAcked(false);
    }
  }, [open]);

  const canConfirm = !destructive || acked;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {body ? <DialogContentText component="div">{body}</DialogContentText> : null}
          {warning ? <Alert severity={destructive ? 'error' : 'warning'}>{warning}</Alert> : null}
          {destructive ? (
            <FormControlLabel
              control={
                <Checkbox checked={acked} onChange={(event) => setAcked(event.target.checked)} />
              }
              label={ackLabel}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('common:action.cancel')}</Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={!canConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
